import type { AnchorHTMLAttributes, PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import { getHomeSectionIdFromHref, useHomeNavigation } from "../../app/HomeNavigationContext";

type ButtonLinkProps = PropsWithChildren<{
  to: string;
  variant?: "primary" | "secondary" | "ghost";
  external?: boolean;
}> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

const buttonBaseClassName =
  "inline-flex items-center justify-center gap-1 rounded-full border px-4 py-3 text-sm font-semibold tracking-[0.02em] transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-300";

const buttonVariantClassNames: Record<NonNullable<ButtonLinkProps["variant"]>, string> = {
  primary:
    "border-brand-400/18 bg-[linear-gradient(135deg,rgba(38,36,43,0.96),rgba(27,26,31,0.92))] text-brand-300 hover:-translate-y-0.5 hover:border-brand-400/55 hover:bg-[linear-gradient(135deg,rgba(38,36,43,0.98),rgba(27,26,31,0.98))]",
  secondary:
    "border-white/10 bg-white/[0.05] text-stone-50 hover:-translate-y-0.5 hover:border-brand-400/45 hover:bg-white/[0.1]",
  ghost:
    "border-white/10 bg-transparent text-stone-50 hover:-translate-y-0.5 hover:border-brand-400/35 hover:bg-white/[0.06]"
};

export function buttonLinkClassName(variant: NonNullable<ButtonLinkProps["variant"]> = "primary", className = "") {
  return [buttonBaseClassName, buttonVariantClassNames[variant], className].filter(Boolean).join(" ");
}

export function ButtonLink({ children, className = "", external, to, variant = "primary", ...rest }: ButtonLinkProps) {
  const buttonClassName = buttonLinkClassName(variant, className);
  const homeNavigation = useHomeNavigation();
  const homeSectionId = getHomeSectionIdFromHref(to);

  if (external) {
    return (
      <a className={buttonClassName} href={to} {...rest}>
        {children}
      </a>
    );
  }

  if (homeNavigation && homeSectionId) {
    const { onClick, ...linkProps } = rest;

    return (
      <Link
        className={buttonClassName}
        data-audio-nav="true"
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented) {
            return;
          }

          event.preventDefault();
          homeNavigation.navigateSection(homeSectionId, "nav");
        }}
        to={to}
        {...linkProps}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link className={buttonClassName} to={to} {...rest}>
      {children}
    </Link>
  );
}
